import { X, Reply, Forward, Trash2, Globe, Copy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface SelectionToolbarProps {
  selectedCount: number;
  onClear: () => void;
  onReply: () => void;
  onForward: () => void;
  onCopy: () => void;
  onDelete: () => void;
  onMarkPublic: () => void;
  canReply?: boolean;
  canDelete?: boolean;
  isLoading?: boolean;
}

export function SelectionToolbar({
  selectedCount,
  onClear,
  onReply,
  onForward,
  onCopy,
  onDelete,
  onMarkPublic,
  canReply = true,
  canDelete = false,
  isLoading = false,
}: SelectionToolbarProps) {
  if (selectedCount === 0) {return null;}

  return (
    <TooltipProvider delayDuration={200}>
      <div className="absolute bottom-20 left-1/2 -translate-x-1/2 z-10 bg-background border shadow-lg rounded-full px-2 py-1.5 flex items-center gap-0.5 animate-in fade-in-0 zoom-in-95 slide-in-from-bottom-2">
        {/* Close button */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-10 w-10 rounded-full hover:bg-muted"
              onClick={onClear}
            >
              <X className="h-5 w-5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top" className="text-xs">
            Отменить выбор
          </TooltipContent>
        </Tooltip>

        {/* Selected count */}
        <span className="text-sm font-medium px-3 min-w-[48px] text-center tabular-nums">
          {selectedCount}
        </span>

        {/* Divider */}
        <div className="h-6 w-px bg-border mx-1" />

        {/* Reply button */}
        {canReply && selectedCount === 1 && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-10 w-10 rounded-full hover:bg-muted"
                onClick={onReply}
                disabled={isLoading}
              >
                <Reply className="h-5 w-5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top" className="text-xs">
              Ответить
            </TooltipContent>
          </Tooltip>
        )}

        {/* Forward button */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-10 w-10 rounded-full hover:bg-muted"
              onClick={onForward}
              disabled={isLoading}
            >
              <Forward className="h-5 w-5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top" className="text-xs">
            Переслать
          </TooltipContent>
        </Tooltip>

        {/* Copy button */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-10 w-10 rounded-full hover:bg-muted"
              onClick={onCopy}
              disabled={isLoading}
            >
              <Copy className="h-5 w-5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top" className="text-xs">
            Копировать
          </TooltipContent>
        </Tooltip>

        {/* Mark public button */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-10 w-10 rounded-full hover:bg-muted text-primary"
              onClick={onMarkPublic}
              disabled={isLoading}
            >
              <Globe className="h-5 w-5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top" className="text-xs">
            Сделать публичным
          </TooltipContent>
        </Tooltip>

        {/* Delete button */}
        {canDelete && (
          <>
            <div className="h-6 w-px bg-border mx-1" />
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-10 w-10 rounded-full hover:bg-destructive/10 text-destructive"
                  onClick={onDelete}
                  disabled={isLoading}
                >
                  <Trash2 className="h-5 w-5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top" className="text-xs">
                Удалить
              </TooltipContent>
            </Tooltip>
          </>
        )}
      </div>
    </TooltipProvider>
  );
}
